import { RootState } from "common/reducers";
import { selectedCheck, selectedIds } from "common/selectors/helper";
import { ids } from "common/selectors/idsManagement.selectors";
import { List } from "immutable";
import { createSelector } from "reselect";

export const rootKey = "presentations";

/**
 * Loads byId key from presentations
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, PresentationRecord>} Map<number, PresentationRecord>
 */
export const byId = (state: RootState, _props?: any) => state.getIn([rootKey, "byId"]);

/**
 * Loads the loading flag from presentations
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} boolean
 */
export const loading = (state: RootState, _props?: any) => state.getIn([rootKey, "loading"]);

/**
 * Loads the presentation using id
 *
 * @param {RootState} state The RootState
 * @param {{ presentationId: number }} options
 * @returns {PresentationRecord} PresentationRecord
 */
export const presentation = (state: RootState, props?: any) => byId(state).get(props.presentationId);

/**
 * Checks the loading flag of a list of presentationIds
 *
 * @param {RootState} state The RootState
 * @param {{ presentationIds: List<number> }} options
 * @returns {boolean} boolean
 */
export const presentationIdsLoading = (state: RootState, props?: any) => {
  return props.presentationIds.some((id: number) => presentation(state, { presentationId: id })?.loading || false);
};

/**
 * Whether or not all presentations are selected. Used for the select all checkbox state
 *
 * @param {RootState} state The RootState
 * @param {{ presentationIds: List<number> }} options
 * @returns {boolean} boolean
 */
export const selectedChecked = createSelector(
  [byId, (_, props) => props],
  (presentationsById, props: { presentationIds: List<number> }): boolean => {
    return selectedCheck(presentationsById, props.presentationIds);
  },
);

/**
 * Gets all the presentation ids that are selected
 *
 * @param {RootState} state The RootState
 * @param {{ presentationIds: List<number> }} options
 * @returns {List<number>} List<number>
 */
export const presentationsSelectedIds = createSelector(
  [byId, ids],
  (presentationsById, presentationIds): List<number | undefined> => {
    return selectedIds(presentationsById, presentationIds as List<number>);
  },
);

/**
 * Whether or not at least one presentation are selected
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const presentationsSelected = createSelector([presentationsSelectedIds], (presentationsSelectedIds): boolean => {
  return presentationsSelectedIds.size > 0;
});

/**
 * Gets the job presentation record, or a default if none exists
 *
 * @param {RootState} state The RootState
 * @returns {PresentationRecord} PresentationRecord
 */
export const jobPresentation = (state: RootState, _props?: any) => {
  const id = ids(state, { modelName: "jobPresentations" })?.first();
  if (!id) return undefined;
  return byId(state).get(id as number, null);
};

/**
 * Gets all presentation records for the current presentation ids
 *
 * @param {RootState} state The RootState
 * @returns {List<PresentationRecord>} List<PresentationRecord>
 */
export const presentationRecords = (state: RootState) => {
  const presentationIDs = ids(state, { modelName: "presentations" }) as List<number>;
  return presentationIDs.map((id) => byId(state).get(id, null)).filter((p) => p !== null && p !== undefined);
};

/**
 * Checks if a slide is checked (linked and not destroyed) in the job presentation
 *
 * @param {RootState} state The RootState
 * @param {{ slideId: number }} props
 * @returns {boolean} boolean
 */
export const slideIsChecked = createSelector(
  [jobPresentation, (_, props: { slideId: number }) => props.slideId],
  (jobPresentation, slideId) => {
    if (!jobPresentation) return false;
    return jobPresentation.dynamic_links.some((dl) => dl.presentable_id === slideId && !dl._destroy);
  },
);
